import { EditIcon, IconButton, LabelChip, type DataTableColumn } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import type { TrainingScheduleRecipeSummary } from "@/api/trainingSchedule";
import { HatTextTooltip } from "@/components/tooltip";
import { DatasetRefCell } from "../DatasetRefCell";
import {
  extraDatasetCount,
  minFreeGpusSentence,
  scheduleStatusChipColor,
  shortSha,
  scheduleTimeLabel,
} from "./scheduleFormat";
import type { ScheduleTableRow } from "./ScheduleTableRow";
import styles from "./SchedulePanel.module.css";

// 행 클릭은 실행 이력 창, 이름은 Recipe 패널, 편집은 이름 칸의 버튼이다(ScheduleTableRow 가 세
// 핸들러를 모두 가진다). 열은 자격·상태와 무관하게 고정이다.
export const SCHEDULE_COLUMNS: readonly DataTableColumn<ScheduleTableRow>[] = [
  {
    key: "name",
    // 이름·설명 스택과 오른쪽 편집 버튼(쓰기 자격)의 폭.
    header: "이름",
    width: "minmax(220px, 1.2fr)",
    render: (row) => <NameCell row={row} />,
  },
  {
    key: "dataset",
    header: "Dataset",
    // registry Dataset 이름은 50자를 자주 넘는다. 그래서 남은 폭을 가장 많이 받는다.
    width: "minmax(300px, 2fr)",
    cellPadding: "tight",
    render: (row) => (
      <DatasetRefCell
        registryRef={row.item.schedule_recipe?.datasets[0]?.registry_ref}
        fallbackLabel={row.dataset}
        extraCount={extraDatasetCount(row.item.schedule_recipe)}
      />
    ),
  },
  {
    key: "config",
    header: "Config",
    width: "minmax(120px, 160px)",
    render: (row) => <ConfigCell recipe={row.item.schedule_recipe} />,
  },
  {
    key: "branchFollow",
    // Yes/No 와 아래 줄 7자 sha 가 들어가는 폭. 뜻은 헤더("Branch 변경 시만")와 툴팁이 표시한다.
    header: "Branch 변경 시만",
    width: "120px",
    render: (row) => <BranchFollowCell row={row} />,
  },
  {
    key: "nextRun",
    // cron 해석("매주 월·수·금요일 09:00")과 아래 줄 시각이 잘리지 않는 폭.
    header: "다음 실행",
    width: "minmax(180px, 220px)",
    render: (row) => (
      <div className={styles.stackedCell}>
        {row.recurrenceDetail === null ? (
          <span>{row.recurrence}</span>
        ) : (
          <HatTextTooltip text={`cron ${row.recurrenceDetail}`}>
            <span>{row.recurrence}</span>
          </HatTextTooltip>
        )}
        <span className={styles.subText}>{row.nextRun}</span>
      </div>
    ),
  },
  {
    key: "gpuGate",
    header: "여유 GPU 조건",
    width: "120px",
    render: (row) => <GpuGateCell row={row} />,
  },
  {
    key: "activeCount",
    // 열 폭이 좁아 두 줄로 쌓는다. c6 헤더는 nowrap 이라 줄바꿈 대신 세로 flex 로 나눈다.
    header: (
      <span className={styles.stackedHeader}>
        <span>실행 중인</span>
        <span>Session</span>
      </span>
    ),
    width: "84px",
    align: "right",
    render: (row) => <span className={styles.numeric}>{row.activeCount}</span>,
  },
  {
    // 맨 끝 열. 칩 폭에 맞춰 좁게 고정하고 오른쪽 정렬해 표 끝에 붙인다(남은 폭은 이름과 Dataset
    // 이 받는다). 마지막 오류는 한 줄에 다 들어가지 않으므로 칩의 툴팁으로 표시한다.
    key: "status",
    header: "Status",
    width: "110px",
    align: "right",
    render: (row) => <StatusCell row={row} />,
  },
];

// 이름 칸. 이름(Recipe 패널 링크)·설명 스택과, 쓰기 자격이 있을 때만 표시하는 편집 버튼이다.
// c6 행 활성화는 중첩 interactive 타깃을 제외한다. 그래서 링크와 버튼은 행 클릭(이력 창)과 섞이지
// 않는다. 명령이 진행 중인 행은 편집 버튼만 잠긴다.
function NameCell({ row }: { row: ScheduleTableRow }): ReactNode {
  const description = row.item.description ?? "";
  const onEdit = row.onEdit;
  return (
    <div className={styles.nameCell}>
      <div className={styles.stackedCell}>
        <button
          type="button"
          className={styles.nameLink}
          title={row.name}
          onClick={() => row.onOpenRecipe(row.item)}
        >
          {row.name}
        </button>
        {description === "" ? null : (
          <span className={styles.subText} title={description}>
            {description}
          </span>
        )}
      </div>
      {onEdit === null ? null : (
        <HatTextTooltip text="편집">
          <IconButton
            size="sm"
            variant="ghost"
            aria-label="편집"
            disabled={row.editLocked}
            onClick={() => onEdit(row.item)}
          >
            <EditIcon />
          </IconButton>
        </HatTextTooltip>
      )}
    </div>
  );
}

// 구성 칸. config 이름과 learning-core 지정(branch/커밋)이다. recipe 는 PATCH 로 바꿀 수 없으므로
// 이 칸은 읽기 전용 사실이다.
function ConfigCell({ recipe }: { recipe: TrainingScheduleRecipeSummary }): ReactNode {
  const configName = recipe?.config?.name ?? "—";
  const learningCoreRef = recipe?.learning_core?.ref ?? null;
  return (
    <div className={styles.stackedCell}>
      <span>{configName}</span>
      {learningCoreRef === null ? null : (
        <span className={styles.subText} title={learningCoreRef}>
          {learningCoreRef}
        </span>
      )}
    </div>
  );
}

// Branch 변경 시만 칸. skip_if_unchanged 와 그 조건이 마지막으로 확인한 Commit 이다.
function BranchFollowCell({ row }: { row: ScheduleTableRow }): ReactNode {
  const { skip_if_unchanged: skip, last_resolved_sha: lastSha } = row.item;
  if (!skip) {
    return (
      <HatTextTooltip text="예정 시각마다 매번 실행합니다.">
        <span className={styles.subText}>{row.branchFollow}</span>
      </HatTextTooltip>
    );
  }
  return (
    <HatTextTooltip text="Branch에 새 Commit이 있을 때만 실행합니다. 아래는 마지막으로 확인한 Commit 입니다.">
      <div className={styles.stackedCell}>
        <span>{row.branchFollow}</span>
        <span className={styles.monoText}>{shortSha(lastSha)}</span>
      </div>
    </HatTextTooltip>
  );
}

// 여유 GPU 조건 칸. 조건 값과, 그 조건이 실행을 보류한 마지막 사유다(있을 때만 툴팁).
function GpuGateCell({ row }: { row: ScheduleTableRow }): ReactNode {
  const { last_defer_reason: deferReason, last_deferred_at: deferredAt } = row.item;
  const sentence = minFreeGpusSentence(row.item.min_free_gpus);
  if (deferReason == null || deferReason === "") {
    if (sentence === null) {
      return <span>{row.gpuGate}</span>;
    }
    return (
      <HatTextTooltip text={sentence}>
        <span>{row.gpuGate}</span>
      </HatTextTooltip>
    );
  }
  return (
    <HatTextTooltip
      text={`마지막으로 실행을 보류한 사유입니다: ${deferReason} (${scheduleTimeLabel(deferredAt)})`}
    >
      <span className={styles.deferredGate}>{row.gpuGate}</span>
    </HatTextTooltip>
  );
}

// 마지막 오류가 있으면 칩 옆에 경고 점을 표시하고 툴팁이 원문을 표시한다. 원문은 한 줄에 다
// 들어가지 않는다. 편집 다이얼로그가 전문을 표시한다.
function StatusCell({ row }: { row: ScheduleTableRow }): ReactNode {
  const lastError = row.item.last_error ?? "";
  const color = scheduleStatusChipColor(row.item.status);
  const chip = (
    <LabelChip color={color} size="xs" variant="filled">
      {row.status}
    </LabelChip>
  );
  if (lastError === "") {
    return chip;
  }
  return (
    <HatTextTooltip text={`마지막 오류: ${lastError}`}>
      <span className={styles.statusWithError}>
        {chip}
        <span className={styles.errorDot} role="img" aria-label="마지막 실행 오류" />
      </span>
    </HatTextTooltip>
  );
}
